import React, { useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import { MailProDialog } from "views/Pma/MailPro/components/MailProDialog/MailProDialog";
import { MailProDialogHeader } from "views/Pma/MailPro/components/MailProDialog/MailProDialogHeader";
import { MailProDialogBody } from "views/Pma/MailPro/components/MailProDialog/MailProDialogBody";
import MailProButton from "views/Pma/MailPro/components/UI/MailProButton";
import { DIALOG_BUTTON_BORDER_RADIUS, DIALOG_BUTTON_MIN_HEIGHT, DISCLAIMER_PAIR_CHAR_LIMIT, RED_PRIMARY } from "./constants";
import ErrorDialog from "./ErrorDialog";

const useStyles = makeStyles({
  dialogPaper: {
    maxWidth: "480px",
    width: "44vw",
    "& .MuiDialogContent-root": { padding: "0 !important" },
  },
  dialogHeader: {
    backgroundColor: "#4A7396",
    color: "white",
    padding: "8px 24px",
    textAlign: "center",
    width: "100%",
  },
  headerTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "center",
  },
  headerSubtitle: {
    fontSize: "15px",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "center",
    opacity: 0.95,
  },
  dialogBody: {
    padding: "14px 24px 10px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  fieldWrap: {
    display: "flex",
    flexDirection: "column",
    gap: "3px",
  },
  label: {
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    color: "#374151",
    fontFamily: "objektiv-mk2, sans-serif",
  },
  readOnlyText: {
    fontSize: "12px",
    fontFamily: "objektiv-mk2, sans-serif",
    color: "#4b5563",
    fontStyle: "italic",
    padding: "2px 0",
  },
  input: {
    width: "100%",
    border: "1px solid #d1d5db",
    borderRadius: "4px",
    fontSize: "12px",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "left",
    height: "26px",
    padding: "4px 8px",
    boxSizing: "border-box",
    resize: "vertical",
    lineHeight: "1.2",
    "&:focus": {
      outline: "none",
      borderColor: "#4A7396",
    },
  },
  inputError: {
    borderColor: RED_PRIMARY,
    backgroundColor: "#fef2f2",
  },
  inputDisabled: {
    backgroundColor: "#f3f4f6",
    color: "#9ca3af",
    cursor: "not-allowed",
  },
  disabledNote: {
    fontSize: "10px",
    color: "#9ca3af",
    fontFamily: "objektiv-mk2, sans-serif",
    fontStyle: "italic",
  },
  pairGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
    padding: "8px",
  },
  counter: {
    fontSize: "10px",
    color: "#6b7280",
    fontFamily: "objektiv-mk2, sans-serif",
  },
  counterError: {
    color: RED_PRIMARY,
    fontWeight: 700,
  },
  dialogFooter: {
    padding: "10px 24px",
    background: "#f9fafb",
    borderTop: "1px solid #e5e7eb",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
    alignItems: "center",
  },
  footerLeft: {
    justifySelf: "start",
  },
  footerCenter: {
    justifySelf: "center",
  },
});

export default function EditTableFooterDialog({
  open,
  onClose,
  areaName = "",
  miniTableDisclaimer1 = "",
  miniTableDisclaimer2 = "",
  miniTableDisclaimer3 = "",
  miniTableDisclaimer4 = "",
  offMarketDisclaimerBool = false,
  coListingDisclaimerBool = false,
  onSave,
  isSaving = false,
}) {
  const classes = useStyles();
  const [disclaimer2, setDisclaimer2] = useState("");
  const [disclaimer3, setDisclaimer3] = useState("");
  const [disclaimer4, setDisclaimer4] = useState("");
  const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);
  const [validationErrorMessages, setValidationErrorMessages] = useState([]);

  useEffect(() => {
    if (!open) return;
    setDisclaimer2(miniTableDisclaimer2 || "");
    setDisclaimer3(miniTableDisclaimer3 || "");
    setDisclaimer4(miniTableDisclaimer4 || "");
  }, [open, miniTableDisclaimer2, miniTableDisclaimer3, miniTableDisclaimer4]);

  const defaultText = miniTableDisclaimer1 || "";
  const pair1Length = defaultText.length + disclaimer2.length;
  const pair2Length = disclaimer3.length + disclaimer4.length;
  const pair1OverLimit = pair1Length > DISCLAIMER_PAIR_CHAR_LIMIT;
  const pair2OverLimit = pair2Length > DISCLAIMER_PAIR_CHAR_LIMIT;

  const handleUpdate = () => {
    const errors = [];
    if (pair1OverLimit) {
      errors.push(
        `Default + Off Market disclaimers combined use ${pair1Length} of ${DISCLAIMER_PAIR_CHAR_LIMIT} maximum allowed characters.`
      );
    }
    if (pair2OverLimit) {
      errors.push(
        `Co Listed + Custom Property Type disclaimers combined use ${pair2Length} of ${DISCLAIMER_PAIR_CHAR_LIMIT} maximum allowed characters.`
      );
    }
    if (errors.length > 0) {
      setValidationErrorMessages(errors);
      setShowValidationErrorDialog(true);
      return;
    }
    onSave?.({
      miniTableDisclaimer2: disclaimer2,
      miniTableDisclaimer3: disclaimer3,
      miniTableDisclaimer4: disclaimer4,
    });
  };

  return (
    <>
      <ErrorDialog
        open={showValidationErrorDialog}
        onClose={() => setShowValidationErrorDialog(false)}
        headerText="PLEASE NOTE:"
        content={
          <div>
            {validationErrorMessages.map((msg, i) => (
              <div key={i}>{msg}</div>
            ))}
          </div>
        }
        headerVariant="red"
        bodyAlign="center"
        dialogProps={{ style: { zIndex: 3000 } }}
      />
      <MailProDialog
        open={open}
        onClose={() => { if (!isSaving) onClose?.(); }}
        disableEscapeKeyDown={isSaving}
        classes={{ paper: classes.dialogPaper }}
      >
        <MailProDialogHeader>
          <div className={classes.dialogHeader}>
            <div className={classes.headerTitle}>{areaName || "EDIT TABLE"}</div>
            <div className={classes.headerSubtitle}>Edit Table Footer</div>
          </div>
        </MailProDialogHeader>
        <MailProDialogBody>
          <div className={classes.dialogBody}>
            <div className={classes.pairGroup}>
              <div className={classes.fieldWrap}>
                <label className={classes.label}>Default (read-only)</label>
                <div className={classes.readOnlyText}>{defaultText || "—"}</div>
              </div>
              <div className={classes.fieldWrap}>
                <label className={classes.label}>Off Market</label>
                {!offMarketDisclaimerBool && (
                  <div className={classes.disabledNote}>No off market property, so field is read only.</div>
                )}
                <textarea
                  className={`${classes.input} ${pair1OverLimit ? classes.inputError : ""} ${!offMarketDisclaimerBool ? classes.inputDisabled : ""}`}
                  value={disclaimer2}
                  onChange={(e) => setDisclaimer2(e.target.value)}
                  placeholder="Enter off market disclaimer text"
                  rows={1}
                  disabled={!offMarketDisclaimerBool}
                />
              </div>
              <div className={`${classes.counter} ${pair1OverLimit ? classes.counterError : ""}`}>
                You have used {pair1Length} of {DISCLAIMER_PAIR_CHAR_LIMIT} maximum allowed characters (Default + Off Market combined).
              </div>
            </div>

            <div className={classes.pairGroup}>
              <div className={classes.fieldWrap}>
                <label className={classes.label}>Co Listed</label>
                {!coListingDisclaimerBool && (
                  <div className={classes.disabledNote}>No co listed property, so field is read only.</div>
                )}
                <textarea
                  className={`${classes.input} ${pair2OverLimit ? classes.inputError : ""} ${!coListingDisclaimerBool ? classes.inputDisabled : ""}`}
                  value={disclaimer3}
                  onChange={(e) => setDisclaimer3(e.target.value)}
                  placeholder="Enter co listed disclaimer text"
                  rows={1}
                  disabled={!coListingDisclaimerBool}
                />
              </div>
              <div className={classes.fieldWrap}>
                <label className={classes.label}>Custom Property Type</label>
                <textarea
                  className={`${classes.input} ${pair2OverLimit ? classes.inputError : ""}`}
                  value={disclaimer4}
                  onChange={(e) => setDisclaimer4(e.target.value)}
                  placeholder="Enter custom property type disclaimer text"
                  rows={1}
                />
              </div>
              <div className={`${classes.counter} ${pair2OverLimit ? classes.counterError : ""}`}>
                You have used {pair2Length} of {DISCLAIMER_PAIR_CHAR_LIMIT} maximum allowed characters (Co Listed + Custom Property Type combined).
              </div>
            </div>
          </div>
        </MailProDialogBody>
        <div className={classes.dialogFooter}>
          <div className={classes.footerLeft}>
            <MailProButton
              variant="filled"
              label="Cancel"
              disabled={isSaving}
              onClick={onClose}
              customStyle={{
                fontSize: "12px",
                padding: "6px 14px",
                textTransform: "uppercase",
                fontWeight: "bold",
                minWidth: "auto",
                borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
                minHeight: DIALOG_BUTTON_MIN_HEIGHT,
                height: DIALOG_BUTTON_MIN_HEIGHT,
                boxSizing: "border-box",
              }}
            />
          </div>
          <div className={classes.footerCenter}>
            <MailProButton
              variant="filled"
              label={isSaving ? "Updating..." : "Update"}
              disabled={isSaving}
              onClick={handleUpdate}
              customStyle={{
                fontSize: "12px",
                padding: "8px 18px",
                textTransform: "uppercase",
                fontWeight: "bold",
                minWidth: "auto",
                borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
                minHeight: DIALOG_BUTTON_MIN_HEIGHT,
                height: DIALOG_BUTTON_MIN_HEIGHT,
                boxSizing: "border-box",
              }}
            />
          </div>
        </div>
      </MailProDialog>
    </>
  );
}
